import { useEffect, useState } from 'react';

/**
 * Ширина экрана как три состояния, а не как набор чисел по местам.
 *
 * До этого в коде жили два разных порога: 768 в оболочке (когда сворачивать
 * меню) и 900 на карте (когда запрещать правку). Числа взялись из разных
 * задач разных фаз, и в промежутке между ними платформа вела себя невнятно:
 * меню уже развёрнуто, а карта ещё только для просмотра.
 *
 * Границы выбраны по устройствам, а не по красоте чисел:
 *
 * - **телефон** — до 640: самый широкий телефон в портретной ориентации это
 *   430 px, дальше начинаются планшеты и узкие окна на десктопе;
 * - **планшет** — 640…1023: места хватает на таблицу, но не на таблицу
 *   рядом с панелью свойств;
 * - **десктоп** — от 1024: то, подо что платформа нарисована.
 *
 * Ландшафтный телефон попадает в «планшет», и это намеренно: 844 px в ширину
 * — это ширина планшета, и таблицу на них показать можно. Высоты там мало,
 * но с высотой разбирается разметка, а не этот модуль.
 */
export type Viewport = 'phone' | 'tablet' | 'desktop';

/** Границы в пикселях. Те же числа продублированы в CSS утилитами Tailwind. */
export const VIEWPORT_MIN = { tablet: 640, desktop: 1024 } as const;

export function viewportOf(width: number): Viewport {
  if (width >= VIEWPORT_MIN.desktop) {
    return 'desktop';
  }

  return width >= VIEWPORT_MIN.tablet ? 'tablet' : 'phone';
}

/**
 * Ширина сейчас.
 *
 * Через `matchMedia`, а не через обработчик `resize`: изменение размера окна
 * приходит десятками событий на одно движение мыши, и каждое из них
 * перерисовывало бы карту. Запрос сообщает только о пересечении границы —
 * то есть ровно тогда, когда что-то должно измениться.
 *
 * Без `matchMedia` (jsdom, старые браузеры) отвечает по `innerWidth` один раз:
 * это хуже, чем слежение, но лучше, чем отказ.
 */
export function useViewport(): Viewport {
  const [viewport, setViewport] = useState<Viewport>(() => viewportOf(window.innerWidth));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }

    const queries = [
      window.matchMedia(`(min-width: ${VIEWPORT_MIN.tablet}px)`),
      window.matchMedia(`(min-width: ${VIEWPORT_MIN.desktop}px)`),
    ];

    const onChange = (): void => setViewport(viewportOf(window.innerWidth));

    for (const query of queries) {
      query.addEventListener('change', onChange);
    }

    // Ширина могла измениться между первым состоянием и подпиской.
    onChange();

    return () => {
      for (const query of queries) {
        query.removeEventListener('change', onChange);
      }
    };
  }, []);

  return viewport;
}

/** Телефон ли это. Самый частый вопрос — отдельным именем, чтобы не сравнивать строки по коду. */
export function useIsPhone(): boolean {
  return useViewport() === 'phone';
}

/**
 * Палец, а не курсор.
 *
 * Отдельно от ширины: узкое окно на ноутбуке — это мышь, а планшет
 * в ландшафте — палец. Размер целей и то, что делается наведением, зависят
 * от указателя, а не от количества пикселей.
 */
export function usePointerIsCoarse(): boolean {
  const [coarse, setCoarse] = useState(
    () => window.matchMedia?.('(pointer: coarse)').matches ?? false,
  );

  useEffect(() => {
    const query = window.matchMedia?.('(pointer: coarse)');

    if (!query) {
      return;
    }

    const onChange = (): void => setCoarse(query.matches);

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return coarse;
}
